// Initials in a coloured disc, for a person, a company or a connected account
// with no picture of its own.
//
// The hue is derived from the label so the same person is the same colour on
// every screen and in every session — no storage, no palette to run out of.
// Only the hue is decided here: saturation and lightness belong to the theme
// (`.swatch` in theme.css), because JavaScript has no business knowing whether
// the page is currently dark (ADR-0014).

import type { CSSProperties } from "react";

/**
 * The letters to draw for a label.
 *
 * `max` is how many words contribute: two for a person or a company, one for a
 * 28px disc in the favourites rail where a second letter is a smudge.
 *
 * A label that survives none of this — an account named in a script the regex
 * strips, or one that is nothing but an emoji — falls back to its own first
 * character rather than to a question mark. "?" is the one thing a monogram
 * must never say: it reads as "we do not know who this is" about an account the
 * person connected themselves.
 */
export function initialsOf(label: string, max = 2): string {
  const words = label.replace(/[^a-zA-Z0-9 ]/g, " ").trim().split(/\s+/).filter(Boolean);
  const initials = words.slice(0, max).map((w) => w[0].toUpperCase()).join("");
  if (initials) return initials;
  // `[...label]` and not `label[0]`: a first character outside the basic plane
  // is two code units, and half an emoji is not a letter.
  const first = [...label.trim()][0];
  return first ? first.toUpperCase() : "•";
}

export function Monogram({
  label, className = "", max = 2,
}: {
  label: string;
  className?: string;
  /** Words that contribute a letter. One for small discs, two elsewhere. */
  max?: number;
}) {
  let hash = 0;
  for (const ch of label) hash = (hash * 31 + ch.charCodeAt(0)) % 360;

  return (
    <span className={`monogram swatch ${className}`.trim()} aria-hidden="true"
          style={{ "--swatch-h": hash } as CSSProperties}>
      {initialsOf(label, max)}
    </span>
  );
}
